import { useRef, useState } from "react";
import { SecondaryButton } from "@/components/admin/AdminUI";
import api from "@/lib/api";
import { toast } from "@/lib/toast";

/**
 * Uploads a CSV to the given admin endpoint and reports per-row results.
 * Endpoint must accept `multipart/form-data` with field name `file` and return
 * `{created, updated, errors, total_rows}`.
 */
export function ImportCsvButton({ endpoint, accept = ".csv,text/csv", label = "Import CSV", testid = "import-csv-button", onSuccess }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  function pick() {
    inputRef.current?.click();
  }

  async function handle(e) {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so picking the same file twice still fires
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { created = 0, updated = 0, errors = [] } = res.data || {};
      if (errors.length === 0) {
        toast.success(`Imported: ${created} created, ${updated} updated`);
      } else {
        // Show first 3 errors inline; full list in console.
        const preview = errors.slice(0, 3).map((er) => `Row ${er.row}: ${er.error}`).join("; ");
        toast.warning(`Imported: ${created} created, ${updated} updated. ${errors.length} row error${errors.length === 1 ? "" : "s"} (${preview})`);
        console.warn("CSV import errors:", errors);
      }
      onSuccess?.(res.data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const msg = detail
        ? `Import failed (${status}): ${detail}`
        : status
          ? `Import failed (HTTP ${status}). Check the browser console for details.`
          : `Import failed: ${e.message || "Network error"}`;
      console.error("CSV import error:", { status, response: e?.response?.data, error: e });
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <SecondaryButton onClick={pick} disabled={busy} data-testid={testid}>
        {busy ? "Importing…" : label}
      </SecondaryButton>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handle}
        className="hidden"
        data-testid={`${testid}-input`}
      />
    </>
  );
}
